import { showMessage } from '../utils';

/**
 * Get share URL with invite code
 * @param {string} [customCode] - Optional custom invite code
 * @returns {string} Complete share URL
 */
export function getShareUrl(customCode) {
  // Priority: passed code, localStorage code, URL parameter code
  const code = customCode || localStorage.getItem('inviteCode') || getInviteCodeFromUrl() || '';
  
  // Determine base path
  let basePath = window.location.pathname;
  if (basePath.startsWith('/campaigns/invitations')) {
    basePath = '/campaigns/invitations';
  } else {
    basePath = '';
  }
  
  return `${window.location.origin}${basePath}/signup-invite-code?inviteCode=${code}`;
}

/**
 * Extract invite code from URL parameters
 * @returns {string|null} Invite code or null
 */
function getInviteCodeFromUrl() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const inviteCode = urlParams.get('inviteCode');
    return inviteCode;
  } catch (error) {
    console.error('Error parsing URL parameters:', error);
    return null;
  }
}

// Share title
export const shareTitle = "Join uTaxes - Get $50 Off Your First Tax Filing!";

/**
 * Share to specified application
 * @param {string} type - Application type
 * @param {string} [url] - Share URL
 */
export function shareToApp(type, url = getShareUrl()) {
  // Use native share API if available
  if (navigator.share && type === 'native') {
    navigator.share({
      title: shareTitle,
      text: "I found this amazing tax software! Use my link to get a discount when you sign up!",
      url: url
    }).catch(error => console.log('Share failed:', error));
    return;
  }

  // Flutter app communication
  const shareData = {
    type: "Share",
    data: {
      title: shareTitle,
      url,
      type,
    },
  };
  
  if (window.flutterApp) {
    window.flutterApp.postMessage(JSON.stringify(shareData));
  }
}

/**
 * Copy link to clipboard
 * @param {string} [url] - URL to copy
 */
export function handleCopyLink(url = getShareUrl()) {
  // Ensure URL is a string
  if (typeof url !== 'string') {
    try {
      url = String(url);
    } catch {
      url = '';
    }
  }

  if (!url) {
    showMessage("No link to copy", "error");
    return;
  }

  // Prefer modern Clipboard API
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url)
      .then(() => {
        showMessage("Link copied successfully!", "success");
      })
      .catch(err => {
        // If Clipboard API fails, use fallback method
        console.warn('Clipboard API failed:', err);
        fallbackCopy(url);
      });
  } else {
    // Browser doesn't support Clipboard API, use fallback
    fallbackCopy(url);
  }
}

/**
 * Fallback copy method (for browsers without Clipboard API support)
 * @param {string} text - Text to copy
 */
function fallbackCopy(text) {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    textarea.style.opacity = "0";
    
    document.body.appendChild(textarea);
    
    // Mobile device handling
    if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
      // iOS special handling
      const editable = textarea.contentEditable;
      const readOnly = textarea.readOnly;
      
      textarea.contentEditable = 'true';
      textarea.readOnly = false;
      
      const range = document.createRange();
      range.selectNodeContents(textarea);
      
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      
      textarea.setSelectionRange(0, 999999);
      textarea.contentEditable = editable;
      textarea.readOnly = readOnly;
    } else {
      // Other devices
      textarea.select();
    }
    
    const successful = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (successful) {
      showMessage("Link copied successfully!", "success");
    } else {
      throw new Error("Copy command failed");
    }
  } catch (err) {
    console.error('Copy failed:', err);
    showMessage("Failed to copy link", "error");
  }
}
